import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function Testimonials() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState(0);

  const testimonials = [
    { quoteKey: 'testimonials.1quote', nameKey: 'testimonials.1name', roleKey: 'testimonials.1role', image: 'https://readdy.ai/api/search-image?query=Professional%20male%20business%20owner%20headshot%20portrait%20warm%20lighting%20clean%20white%20background%20friendly%20expression%20modern%20style%20editorial%20photography&width=160&height=160&seq=testimonial-01&orientation=squarish' },
    { quoteKey: 'testimonials.2quote', nameKey: 'testimonials.2name', roleKey: 'testimonials.2role', image: 'https://readdy.ai/api/search-image?query=Professional%20female%20marketing%20director%20headshot%20portrait%20warm%20lighting%20clean%20white%20background%20confident%20expression%20modern%20business%20style%20editorial%20photography&width=160&height=160&seq=testimonial-02&orientation=squarish' },
    { quoteKey: 'testimonials.3quote', nameKey: 'testimonials.3name', roleKey: 'testimonials.3role', image: 'https://readdy.ai/api/search-image?query=Professional%20male%20entrepreneur%20headshot%20portrait%20warm%20lighting%20clean%20white%20background%20confident%20expression%20modern%20business%20style%20editorial%20photography&width=160&height=160&seq=testimonial-03&orientation=squarish' },
    { quoteKey: 'testimonials.4quote', nameKey: 'testimonials.4name', roleKey: 'testimonials.4role', image: 'https://readdy.ai/api/search-image?query=Professional%20female%20attorney%20headshot%20portrait%20warm%20lighting%20clean%20white%20background%20confident%20expression%20modern%20business%20style%20editorial%20photography&width=160&height=160&seq=testimonial-04&orientation=squarish' },
    { quoteKey: 'testimonials.5quote', nameKey: 'testimonials.5name', roleKey: 'testimonials.5role', image: 'https://readdy.ai/api/search-image?query=Professional%20male%20director%20headshot%20portrait%20warm%20lighting%20clean%20white%20background%20confident%20expression%20modern%20business%20style%20editorial%20photography&width=160&height=160&seq=testimonial-05&orientation=squarish' },
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <ScrollReveal direction="up" duration={0.7}>
          <div className="text-center mb-14 md:mb-18">
            <span className="inline-block px-4 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold tracking-wider uppercase mb-5">
              {t('testimonials.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {t('testimonials.title')}{' '}
              <span className="text-primary-500">{t('testimonials.titleHighlight')}</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto">
              {t('testimonials.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <div className="relative max-w-4xl mx-auto">
          <div className="text-8xl md:text-9xl font-serif text-red-100 absolute -top-8 -left-4 select-none leading-none">
            &ldquo;
          </div>

          <div className="relative overflow-hidden min-h-[300px] flex items-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="w-full px-4"
              >
                <div className="text-center">
                  <motion.img
                    src={testimonials[current].image}
                    alt={t(testimonials[current].nameKey)}
                    className="w-20 h-20 rounded-full object-cover mx-auto mb-6 border-2 border-red-200"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                  />
                  <div className="flex justify-center gap-1 mb-6">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="text-gray-800 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl mx-auto">
                    &ldquo;{t(testimonials[current].quoteKey)}&rdquo;
                  </blockquote>
                  <div className="font-heading font-semibold text-gray-900 text-base">{t(testimonials[current].nameKey)}</div>
                  <div className="text-gray-500 text-sm">{t(testimonials[current].roleKey)}</div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-6 mt-8">
            <button
              onClick={() => {
                setDirection(-1);
                setCurrent((current - 1 + testimonials.length) % testimonials.length);
                setIsAutoPlaying(false);
              }}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-red-600 hover:border-red-500 transition-all cursor-pointer"
              aria-label={t('testimonials.prev')}
            >
              <i className="ri-arrow-left-line"></i>
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); setIsAutoPlaying(false); }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    i === current ? 'w-6 bg-primary-500' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Testimonial ${i + 1}`}
                ></button>
              ))}
            </div>

            <button
              onClick={() => {
                setDirection(1);
                setCurrent((current + 1) % testimonials.length);
                setIsAutoPlaying(false);
              }}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-red-600 hover:border-red-500 transition-all cursor-pointer"
              aria-label={t('testimonials.next')}
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}